import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/axios-utils";
import { useConnectionStore } from "@/features/tutor/store/useConnectionStore";
import {
  useSpaceStore,
  type AiMode,
  type SpaceStatus,
} from "../store/useSpaceStore";
import type { TldrawCameraVector } from "../spacesPresenceUtils";
import { useSpace } from "./useSpace";
import { useSpaceCtrl } from "./useSpaceCtrl";

// Empirically: gateway mode-change reconnects complete within ~1 s. Hold the
// disconnect for slightly longer so quick reconnects don't flicker the panel.
const PARTICIPANT_DISCONNECT_DEBOUNCE_MS = 3_000;

export type TeacherSpaceInfo = {
  lessonIds: number[];
  lessonImported: boolean;
  aiMode?: AiMode;
  spaceStatus?: string;
  spaceName?: string;
  teacherName?: string | null;
  teacherId?: number | null;
  userId?: number;
  initialSnapshot?: Record<string, unknown> | null;
  snapshotSavedAt?: string | null;
  startedAt?: string | null;
  totalEnrolled?: number;
};

type UseTeacherSpaceSessionArgs = {
  spaceId: string | undefined;
  token: string | null;
};

export type TeacherSpaceSession = {
  spaceInfo: TeacherSpaceInfo | null;
  spaceStatus: SpaceStatus | "loading";
  setSpaceStatus: (s: SpaceStatus | "loading") => void;
  // Last saved timestamp surfaced by manual-save / SSE snapshot_saved updates.
  teacherLastSaved: Date | null;
  setTeacherLastSaved: (d: Date | null) => void;
  // Last-known live camera per student, fed by `student_camera` ctrl messages.
  // The teacher's StudentWork view reads this to seed initial / live camera.
  studentCameras: Map<string, TldrawCameraVector>;
};

/**
 * Owns the teacher's join lifecycle, ctrl-channel wiring, participant
 * disconnect-debounce, and the studentCameras map. Pulled out of
 * TeacherSpacePage so the page itself only contains layout + canvas plumbing.
 */
export function useTeacherSpaceSession({
  spaceId,
  token,
}: UseTeacherSpaceSessionArgs): TeacherSpaceSession {
  const navigate = useNavigate();
  const { fetchParticipants, fetchConnected } = useSpace();

  const [spaceStatus, setSpaceStatus] = useState<SpaceStatus | "loading">(
    "loading",
  );
  const [spaceInfo, setSpaceInfo] = useState<TeacherSpaceInfo | null>(null);
  const [teacherLastSaved, setTeacherLastSaved] = useState<Date | null>(null);
  const [studentCameras, setStudentCameras] = useState<
    Map<string, TldrawCameraVector>
  >(new Map());

  // Debounced participant_left callbacks check this at fire-time instead of
  // capturing spaceStatus in the outer closure (which would be stale if the
  // space closed during the 3s debounce window).
  const spaceStatusRef = useRef<SpaceStatus | "loading">(spaceStatus);
  spaceStatusRef.current = spaceStatus;

  // Debounce participant disconnects: wait before removing from connected set;
  // cancel if they rejoin within the window.
  const disconnectTimersRef = useRef<
    Map<number, ReturnType<typeof setTimeout>>
  >(new Map());
  useEffect(() => {
    const timers = disconnectTimersRef.current;
    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  // Initial join — runs once per spaceId. Loads participants for live spaces;
  // closed/draft spaces just set the status and skip the live wiring.
  useEffect(() => {
    if (!spaceId) return;
    api
      .post<TeacherSpaceInfo>(`/spaces/${spaceId}/join`)
      .then((res) => {
        setSpaceInfo(res.data);
        if (res.data.aiMode)
          useSpaceStore.getState().setAiMode(res.data.aiMode);
        if (res.data.spaceStatus === "closed") {
          setSpaceStatus("closed");
          if (res.data.spaceName) {
            useSpaceStore
              .getState()
              .setSpace(spaceId, res.data.spaceName, null);
          }
        } else if (res.data.spaceStatus === "draft") {
          setSpaceStatus("draft");
          if (res.data.spaceName) {
            useSpaceStore
              .getState()
              .setSpace(spaceId, res.data.spaceName, null);
          }
        } else {
          setSpaceStatus(res.data.spaceStatus === "paused" ? "paused" : "open");
          if (res.data.spaceName) {
            useSpaceStore
              .getState()
              .setSpace(spaceId, res.data.spaceName, null);
            useSpaceStore
              .getState()
              .setStartedAt(
                res.data.startedAt ? new Date(res.data.startedAt) : null,
              );
            useSpaceStore
              .getState()
              .setTotalEnrolled(res.data.totalEnrolled ?? 0);
            fetchParticipants(spaceId);
            fetchConnected(spaceId).then((ids) => {
              useSpaceStore.getState().setConnectedUserIds(ids.map(Number));
            });
          }
        }
      })
      .catch((err) =>
        console.error("[useTeacherSpaceSession] join failed:", err),
      );
  }, [spaceId, fetchParticipants, fetchConnected]);

  // Surface the persisted snapshot timestamp into local state so the TopBar
  // "last saved" indicator picks it up. Updates on manual save or SSE.
  useEffect(() => {
    if (spaceInfo?.snapshotSavedAt)
      setTeacherLastSaved(new Date(spaceInfo.snapshotSavedAt));
  }, [spaceInfo?.snapshotSavedAt]);

  useSpaceCtrl(
    spaceStatus === "open" || spaceStatus === "paused" ? spaceId : undefined,
    token,
    {
      onConnectionChange: (isConnected) => {
        const store = useConnectionStore.getState();
        if (isConnected) store.markConnected();
        else store.markDisconnected();
      },
      onStateSnapshot: (snap) => {
        // Recover authoritative space-level state on every (re)connect.
        // Closed → navigate to recap; pause/resume → reflect status.
        if (snap.spaceStatus === "closed") {
          setSpaceStatus("closed");
          navigate(`/spaces/${spaceId}/recap`);
          return;
        }
        if (snap.spaceStatus === "paused") setSpaceStatus("paused");
        else if (snap.spaceStatus === "open") setSpaceStatus("open");
        if (useSpaceStore.getState().aiMode !== snap.aiMode) {
          useSpaceStore.getState().setAiMode(snap.aiMode);
        }
      },
      onWriteModeChange: () => {},
      onSpaceClosed: () => {
        setSpaceStatus("closed");
        navigate(`/spaces/${spaceId}/recap`);
      },
      onSpacePaused: () => {
        setSpaceStatus("paused");
      },
      onSpaceResumed: () => {
        setSpaceStatus("open");
      },
      onParticipantJoined: (userId, joinedAt) => {
        if (!spaceId) return;
        const uid = Number(userId);
        // Cancel any pending disconnect debounce for this user
        const pending = disconnectTimersRef.current.get(uid);
        if (pending !== undefined) {
          clearTimeout(pending);
          disconnectTimersRef.current.delete(uid);
        }
        // Re-fetch from DB on join so writeMode is always authoritative.
        fetchParticipants(spaceId).then(() => {
          useSpaceStore.getState().updateParticipantStatus(uid, "just_joined");
        });
        useSpaceStore.getState().addConnected(uid);
        if (joinedAt !== undefined) {
          useSpaceStore
            .getState()
            .updateParticipantJoinedAt(uid, new Date(joinedAt));
        }
      },
      // participant_left = temporary disconnect (e.g. mode change) — debounce
      // before removing from the connected set so brief reconnects don't flicker
      // the panel. Closed-space leaves are ignored: panel is going away anyway.
      // Read spaceStatusRef at fire-time so a close that lands during the
      // debounce window doesn't trigger a removeConnected on a torn-down space.
      onParticipantLeft: (userId) => {
        if (spaceStatusRef.current === "closed") return;
        const uid = Number(userId);
        if (disconnectTimersRef.current.has(uid)) return;
        const timer = setTimeout(() => {
          disconnectTimersRef.current.delete(uid);
          if (spaceStatusRef.current === "closed") return;
          useSpaceStore.getState().removeConnected(uid);
        }, PARTICIPANT_DISCONNECT_DEBOUNCE_MS);
        disconnectTimersRef.current.set(uid, timer);
      },
      onParticipantStatus: (userId, status) => {
        useSpaceStore
          .getState()
          .updateParticipantStatus(Number(userId), status);
      },
      onSnapshotSaved: (savedAt) => {
        setTeacherLastSaved(savedAt);
      },
      onStudentCamera: (userId, camera) => {
        setStudentCameras((prev) => {
          const next = new Map(prev);
          next.set(userId, camera);
          return next;
        });
      },
    },
  );

  return {
    spaceInfo,
    spaceStatus,
    setSpaceStatus,
    teacherLastSaved,
    setTeacherLastSaved,
    studentCameras,
  };
}
